import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import ContentImage from '../Image/ContentImage';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import BlockDataForm from '@plone/volto/components/manage/Form/BlockDataForm';
import { HeroSchema, HeroView } from '../Hero/Hero';
import { ButtonSchema } from '../Button/Button';
import { ChevronRightIcon } from '../SiteChrome/icons';

export const SliderSchema = ({ intl }) => {
  const hero = HeroSchema({ intl });
  const button = ButtonSchema({ intl });
  return {
    title: intl.formatMessage({ id: 'Slider', defaultMessage: 'Slider' }),
    fieldsets: [{ id: 'default', title: 'Default', fields: ['slides'] }],
    properties: {
      slides: {
        title: intl.formatMessage({ id: 'Slides', defaultMessage: 'Slides' }),
        widget: 'object_list',
        schema: {
          title: intl.formatMessage({ id: 'Slide', defaultMessage: 'Slide' }),
          fieldsets: [
            {
              id: 'default',
              title: 'Default',
              fields: ['image', 'alt', 'title', 'text', 'label', 'href'],
            },
          ],
          properties: {
            image: hero.properties.image,
            alt: hero.properties.alt,
            title: hero.properties.title,
            text: hero.properties.text,
            label: {
              title: intl.formatMessage({
                id: 'Button label',
                defaultMessage: 'Button label',
              }),
            },
            href: button.properties.href,
          },
          required: ['title'],
        },
      },
    },
    required: [],
  };
};

export function SliderView({
  data,
  isEditMode = false,
  selectedSlide = undefined,
  onSelectSlide = undefined,
}) {
  const intl = useIntl();
  const slides = (data.slides || []).filter(
    (slide) => isEditMode || slide.title || slide.image?.[0]?.['@id'],
  );
  const count = slides.length;
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const autoplay =
    count > 1 && !isEditMode && !hovered && !focused && !reducedMotion;
  useEffect(() => {
    if (!autoplay) return;
    const timer = setInterval(
      () => setIndex((current) => (current + 1) % count),
      6000,
    );
    return () => clearInterval(timer);
  }, [autoplay, count]);
  const activeIndex =
    isEditMode && selectedSlide
      ? Math.max(
          0,
          slides.findIndex((slide) => slide['@id'] === selectedSlide),
        )
      : Math.min(index, count - 1);
  const select = (next) => {
    const value = (next + count) % count;
    setIndex(value);
    onSelectSlide?.(slides[value]['@id']);
  };
  if (!count)
    return isEditMode ? (
      <p>
        {intl.formatMessage({
          id: 'Add slides in the sidebar.',
          defaultMessage: 'Add slides in the sidebar.',
        })}
      </p>
    ) : null;
  return (
    <section
      className="block bonn-slider"
      aria-roledescription={intl.formatMessage({
        id: 'carousel',
        defaultMessage: 'carousel',
      })}
      aria-label={intl.formatMessage({
        id: 'Slider',
        defaultMessage: 'Slider',
      })}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setFocused(false);
      }}
    >
      <ul
        className="bonn-slider-slides"
        aria-live={autoplay ? 'off' : 'polite'}
      >
        {slides.map((slide, i) => {
          const active = i === activeIndex;
          const href = slide.href?.[0]?.['@id'];
          return (
            <li
              key={slide['@id']}
              className="bonn-slide"
              data-active={active}
              role="group"
              aria-roledescription={intl.formatMessage({
                id: 'Slide',
                defaultMessage: 'Slide',
              })}
              aria-label={intl.formatMessage(
                {
                  id: '{position} of {count}',
                  defaultMessage: '{position} of {count}',
                },
                { position: i + 1, count },
              )}
              aria-hidden={!active}
              inert={active ? undefined : ''}
            >
              <HeroView
                data={slide}
                isEditMode={isEditMode}
                titleTag="h2"
                action={
                  slide.label &&
                  href && (
                    <p className="bonn-slide-button">
                      {isEditMode ? (
                        <span className="bonn-button-link">{slide.label}</span>
                      ) : (
                        <UniversalLink href={href} className="bonn-button-link">
                          {slide.label}
                        </UniversalLink>
                      )}
                    </p>
                  )
                }
              />
            </li>
          );
        })}
      </ul>
      {count > 1 && (
        <div className="bonn-slider-controls">
          <ol className="bonn-slider-pagination">
            {slides.map((slide, i) => {
              const asset = slide.image?.[0];
              return (
                <li key={slide['@id']}>
                  <button
                    type="button"
                    className="bonn-slider-preview"
                    aria-current={i === activeIndex ? 'true' : undefined}
                    aria-label={intl.formatMessage(
                      {
                        id: 'Slide {position}: {title}',
                        defaultMessage: 'Slide {position}: {title}',
                      },
                      { position: i + 1, title: slide.title || '' },
                    )}
                    onClick={() => select(i)}
                  >
                    {
                      <ContentImage
                        fallback="hero"
                        className="bonn-slider-thumb"
                        item={asset?.image_scales ? asset : undefined}
                        src={
                          asset?.image_scales
                            ? undefined
                            : asset?.['@id']
                              ? `${flattenToAppURL(asset['@id'])}/@@images/image/mini`
                              : undefined
                        }
                        alt=""
                        sizes="76px"
                        loading="lazy"
                      />
                    }
                    <span className="bonn-slider-preview-title">
                      {slide.title}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
          <div className="bonn-slider-arrows">
            <button
              type="button"
              className="bonn-slider-arrow bonn-slider-prev"
              aria-label={intl.formatMessage({
                id: 'Previous slide',
                defaultMessage: 'Previous slide',
              })}
              onClick={() => select(activeIndex - 1)}
            >
              <ChevronRightIcon size={20} />
            </button>
            <button
              type="button"
              className="bonn-slider-arrow"
              aria-label={intl.formatMessage({
                id: 'Next slide',
                defaultMessage: 'Next slide',
              })}
              onClick={() => select(activeIndex + 1)}
            >
              <ChevronRightIcon size={20} />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export function SliderEdit(props) {
  const {
    data,
    block,
    selected,
    onChangeBlock,
    blocksErrors,
    navRoot,
    contentType,
  } = props;
  const [selectedSlide, setSelectedSlide] = useState();
  const intl = useIntl();
  const schema = SliderSchema({ intl });
  return (
    <>
      <SliderView
        data={data}
        isEditMode
        selectedSlide={selectedSlide}
        onSelectSlide={setSelectedSlide}
      />
      <SidebarPortal selected={selected}>
        <BlockDataForm
          schema={schema}
          title={schema.title}
          formData={data}
          block={block}
          onChangeField={(id, value) => {
            if (id === 'slides') {
              const changed = value.find(
                (slide) =>
                  data.slides?.find((old) => old['@id'] === slide['@id']) !==
                  slide,
              );
              if (changed) setSelectedSlide(changed['@id']);
            }
            onChangeBlock(block, { ...data, [id]: value });
          }}
          onChangeBlock={onChangeBlock}
          errors={blocksErrors}
          navRoot={navRoot}
          contentType={contentType}
        />
      </SidebarPortal>
    </>
  );
}
